import { Component, forwardRef, ChangeDetectionStrategy, ElementRef, HostListener, input, output, signal, computed, effect, viewChild } from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type SliderSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'ui-slider',
  standalone: true,
  imports: [],
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SliderComponent),
      multi: true
    }
  ]
})
export class SliderComponent implements ControlValueAccessor {
  readonly min = input<number>(0);
  readonly max = input<number>(100);
  readonly step = input<number>(1);

  // disabled is mutated internally via setDisabledState — use aliased input + writable signal
  readonly disabledInput = input<boolean>(false, { alias: 'disabled' });
  readonly disabled = signal(false);

  readonly size = input<SliderSize>('md');
  readonly showValue = input<boolean>(false);
  readonly showMinMax = input<boolean>(false);
  readonly id = input<string | undefined>(undefined);
  readonly name = input<string | undefined>(undefined);

  // Accessibility inputs
  readonly ariaLabel = input<string | undefined>(undefined);
  readonly ariaLabelledBy = input<string | undefined>(undefined);
  readonly ariaDescribedBy = input<string | undefined>(undefined);

  readonly sliderChange = output<number>();

  readonly trackRef = viewChild.required<ElementRef<HTMLDivElement>>('track');

  readonly value = signal(0);
  readonly isDragging = signal(false);

  private onChange: (value: number) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    effect(() => this.disabled.set(this.disabledInput()));
  }

  /** Calculate percentage for display */
  readonly percentage = computed<number>(() => {
    return ((this.value() - this.min()) / (this.max() - this.min())) * 100;
  });

  /** Track classes */
  readonly trackClasses = computed<string>(() => {
    const baseClasses = [
      'relative',
      'w-full',
      'rounded-full',
      'bg-bb-bg-tertiary',
      'cursor-pointer'
    ];

    const sizeClasses: Record<SliderSize, string> = {
      sm: 'h-1',
      md: 'h-1.5',
      lg: 'h-2'
    };

    if (this.disabled()) {
      baseClasses.push('cursor-not-allowed', 'opacity-50');
    }

    return [...baseClasses, sizeClasses[this.size()]].join(' ');
  });

  /** Fill classes */
  readonly fillClasses = computed<string>(() => {
    const baseClasses = [
      'absolute',
      'left-0',
      'top-0',
      'h-full',
      'rounded-full',
      'bg-bb-accent-blue',
      'transition-all',
      'duration-75'
    ];

    return baseClasses.join(' ');
  });

  /** Thumb classes */
  readonly thumbClasses = computed<string>(() => {
    const baseClasses = [
      'absolute',
      'top-1/2',
      '-translate-y-1/2',
      '-translate-x-1/2',
      'rounded-full',
      'bg-white',
      'shadow-lg',
      'border-2',
      'border-bb-accent-blue',
      'transition-transform',
      'duration-75',
      'hover:scale-110',
      'focus:outline-none',
      'focus-visible:ring-2',
      'focus-visible:ring-bb-accent-blue',
      'focus-visible:ring-offset-2',
      'focus-visible:ring-offset-bb-bg-primary'
    ];

    const sizeClasses: Record<SliderSize, string[]> = {
      sm: ['w-3', 'h-3'],
      md: ['w-5', 'h-5'],
      lg: ['w-6', 'h-6']
    };

    if (this.disabled()) {
      baseClasses.push('cursor-not-allowed');
    } else {
      baseClasses.push('cursor-grab');
      if (this.isDragging()) {
        baseClasses.push('cursor-grabbing', 'scale-110');
      }
    }

    return [...baseClasses, ...sizeClasses[this.size()]].join(' ');
  });

  /** Value label classes */
  readonly valueLabelClasses = computed<string>(() => {
    const baseClasses = [
      'text-bb-text-primary',
      'font-medium',
      'tabular-nums'
    ];

    const sizeClasses: Record<SliderSize, string> = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base'
    };

    return [...baseClasses, sizeClasses[this.size()]].join(' ');
  });

  /** Min/Max label classes */
  readonly minMaxClasses = computed<string>(() => {
    const baseClasses = [
      'text-bb-text-secondary',
      'tabular-nums'
    ];

    const sizeClasses: Record<SliderSize, string> = {
      sm: 'text-xs',
      md: 'text-xs',
      lg: 'text-sm'
    };

    return [...baseClasses, sizeClasses[this.size()]].join(' ');
  });

  /** Handle track click */
  onTrackClick(event: MouseEvent): void {
    if (this.disabled()) {return;}
    this.updateValueFromEvent(event);
  }

  /** Handle thumb drag start */
  onThumbMouseDown(event: MouseEvent): void {
    if (this.disabled()) {return;}
    event.preventDefault();
    this.isDragging.set(true);
  }

  /** Handle mouse move (global listener when dragging) */
  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging()) {return;}
    this.updateValueFromEvent(event);
  }

  /** Handle mouse up (global listener) */
  @HostListener('window:mouseup')
  onMouseUp(): void {
    if (this.isDragging()) {
      this.isDragging.set(false);
      this.onTouched();
    }
  }

  /** Handle keyboard navigation */
  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled()) {return;}

    let newValue = this.value();

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault();
        newValue = Math.max(this.min(), this.value() - this.step());
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault();
        newValue = Math.min(this.max(), this.value() + this.step());
        break;
      case 'Home':
        event.preventDefault();
        newValue = this.min();
        break;
      case 'End':
        event.preventDefault();
        newValue = this.max();
        break;
      case 'PageDown':
        event.preventDefault();
        newValue = Math.max(this.min(), this.value() - this.step() * 10);
        break;
      case 'PageUp':
        event.preventDefault();
        newValue = Math.min(this.max(), this.value() + this.step() * 10);
        break;
      default:
        return;
    }

    this.setValue(newValue);
  }

  /** Update value from mouse event */
  private updateValueFromEvent(event: MouseEvent): void {
    const track = this.trackRef().nativeElement;
    const rect = track.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const rawValue = this.min() + percentage * (this.max() - this.min());
    const steppedValue = Math.round(rawValue / this.step()) * this.step();
    this.setValue(steppedValue);
  }

  /** Set value and emit changes */
  private setValue(newValue: number): void {
    const clampedValue = Math.max(this.min(), Math.min(this.max(), newValue));
    if (clampedValue !== this.value()) {
      this.value.set(clampedValue);
      this.onChange(clampedValue);
      this.sliderChange.emit(clampedValue);
    }
  }

  // ControlValueAccessor methods
  writeValue(value: number): void {
    this.value.set(value ?? this.min());
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
